import React from "react";
import styled, { css } from "styled-components";
import { compose, withHandlers, withState } from "recompose";
import withAnonymousLogin from "../../core/withAnonymousLogin";
import { withAuthenticatedLogin } from "../../core/withAuthenticatedLogin";
import BackButtonForSteps from "./Steps/BackButton";
import InputForSteps from "./Steps/Input";
import ErrorForSteps from "./Steps/Error";
import GoToCreatorKitModal from "./GoToCreatorKitModal";

const LoginFlow = ({
  form: { username, email, step, error },
  privateOpen,
  handleChangeEmail,
  handleChangeName,
  handleFocusName,
  handlePrevStep,
  handleNextStep,
  onPressSubmit,
  setEmailInput,
  onPressContinue,
  disableAnonymousLogin,
  ...props
}) => {
  return (
    <Container>
      <Steps>
        <InnerSteps step={step}>
          <StepHolder>
            <Tagline hasError={!!error}>
              Life is all about experiences. Find your Top Nine Instagram moments from 2020 and share them with
              your friends.
            </Tagline>

            <InputForSteps
              withInput={!disableAnonymousLogin}
              value={username}
              inputType="text"
              placeholder="Enter your Instagram username"
              onKeyPress={(event) => event.key == "Enter" && onPressContinue()}
              onChange={({ target: { value } }) => handleChangeName(value)}
              onFocus={handleFocusName}
              onClick={onPressContinue}
              buttonText={"Continue"}
            />
            {error ? <ErrorForSteps>{error}</ErrorForSteps> : null}
          </StepHolder>
          <StepHolder>
            <BackButtonForSteps handlePrevStep={handlePrevStep} />
            <Text>
              Some accounts may take more than a couple of seconds to analyze. We will email your Top Nine so you
              don't have to wait!
            </Text>
            <SubText>
              This consent can be revoked at any time on
              <Link href="https://topnine.co/forget-me"> topnine.co/forget-me</Link>
            </SubText>
            <InputForSteps
              value={email}
              inputRef={setEmailInput}
              placeholder="Enter your email"
              onKeyPress={(event) => event.key == "Enter" && onPressSubmit()}
              onChange={({ target: { value } }) => handleChangeEmail(value)}
              onClick={onPressSubmit}
              buttonText={"Find My Top Nine"}
            />
            {error ? <ErrorForSteps>{error}</ErrorForSteps> : null}
          </StepHolder>
          <StepHolder>
            <Loading>Loading...</Loading>
          </StepHolder>
          <StepHolder>
            <Title>Your Top Nine is almost ready!</Title>
            <Text>
              We'll email it to you when it's done! Meanwhile you can get any of your best nine photos printed on a
              variety of products like canvases and phone cases on the App.
            </Text>
            <ButtonContainer>
              <PrimaryButton onClick={handleNextStep}>Find other Top Nine</PrimaryButton>
            </ButtonContainer>
          </StepHolder>
        </InnerSteps>
      </Steps>
      { props.showingModal ? (
        <GoToCreatorKitModal
          active={props.showingModal}
          onClose={() => props.setShowingModal(false)}
          username={username}
          email={email}
        />
      ) : null}
    </Container>
  );
};

const Tagline = styled.h1`
  margin-top: 0px;
  margin-bottom: 32px;
  color: white;
  text-align: left;
  @media screen and (max-width: 500px) {
    top: ${(p) => (p.hasError ? 0 : -20)}px;
    position: relative;
    font-size: 24px;
    line-height: 36px;
    margin: 0;
    padding: 0;
  }
`;

const Loading = styled.span`
  font-family: Montserrat;
  font-weight: 700;
  color: white;
  font-size: 24px;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  width: 100%;
`;

const Button = styled.button`
  width: 100%;
  max-height: 60px;
  display: flex;
  position: relative;
  justify-content: center;
  border-radius: 4px;
  background: black;
  border: 2px solid currentColor;
  border-color: transparent;
  color: #fff;
  white-space: nowrap;
  font-weight: 500;
  line-height: 1.4;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px 16px;
  font-family: Montserrat;
  font-weight: 700;
  color: white;
  font-size: 15px;
  text-transform: uppercase;
`;

const PrimaryButton = styled(Button)`
  background: #3e8dff;
  flex: 2;
`;
const StepHolder = styled.div`
  min-width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Steps = styled.div`
  width: 100%;
  overflow: hidden;
`;

const InnerSteps = styled.div`
  display: flex;
  transition: 0.5s;
  ${(props) => css`
    transform: translateX(-${props.step * 100}%);
    ${StepHolder} {
      opacity: 0;
      //transition: opacity 0.5s;
      &:nth-child(${props.step + 1}) {
        opacity: 1;
      }
    }
  `};
`;

const Title = styled.h4`
  text-align: center;
  font-family: Montserrat;
  font-weight: 700;
  color: white;
  font-size: 24px;
  margin-bottom: 8px;
`;

const Text = styled.p`
  text-align: left;
  font-family: Montserrat;
  font-weight: 700;
  color: white;
  font-size: 22px;
  margin-bottom: 0px !important;
  @media screen and (max-width: 500px) {
    margin-bottom: 0px !important;
  }
`;

const SubText = styled(Text)`
  font-size: 13px !important;
  font-family: Montserrat;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px !important;
  margin-bottom: 32px !important;
`;

const Link = styled.a`
  font-weight: 800;
  font-size: 13px !important;
  cursor: pointer;
  font-family: Montserrat;
  font-weight: 500;
  color: white;
`;

export default compose(
  withAuthenticatedLogin,
  withAnonymousLogin,
  withState("showingModal", "setShowingModal", false),
  withHandlers({
    onPressSubmit: (props) => () => {
      props.setShowingModal(true);
      // if (props.disableAnonymousLogin) {
      //   props.onClickManualTopNine({
      //     email: props.form.email,
      //     handleNextStep: props.handleNextStep,
      //     handlePrevStep: props.handlePrevStep,
      //   });
      // } else {
      //   props.handleSubmit();
      // }
    },
    onPressContinue: (props) => () => {
      props.setShowingModal(true);

      //if (props.disableAnonymousLogin) {
        //props.handleNextStep();
      //} else {
        //props.handleContinue();
      //}
    },
    handleFocusName: (props) => () => {
      props.setShowingModal(true);
    },
  })
)(LoginFlow);
